import { useTranslation } from '@klv/hooks/useTranslation';

import AnimationOutline from '@klv/atoms/animation-outline';
import { Anchor } from '@klv/atoms/anchor';
import BlogAuthorThumbnail from '@klv/molecules/blog-author-thumbnail';
import BlogCategoryButton from '@klv/molecules/blog-category-button';
import Icon from '@klv/atoms/icon';
import TranslatedDate from '@klv/atoms/translated-date';
import { generateClassName } from '@klv/shared/utils/functions';

import * as styles from './styles/blogCard.module.scss';

import type { IBlogCard, IBlogCardImage } from './types';

const BlogCardImage = ({
  aspectRatio,
  backgroundColor = 'light',
  featuredImage,
  featuredImageSrc,
}: IBlogCardImage) => {
  const { t } = useTranslation();

  if (featuredImageSrc || featuredImage?.sourceUrl) {
    return (
      <img
        alt=''
        className={styles.image}
        sizes='(max-width: 575px) 535px, (max-width: 991px) 448px, (max-width: 1023px) 436px, (max-width: 1199px) 355px, (max-width: 1365px) 399px, 396px'
        src={featuredImageSrc || featuredImage?.sourceUrl}
        srcSet={featuredImage?.srcSet}
        style={{ aspectRatio }}
      />
    );
  }

  return (
    <div className={generateClassName([styles.imagePlaceholderContainer, styles[backgroundColor]])}>
      <Icon
        aria-label={`${t('molecules_blogAuthor_authorAltText')}`}
        className={generateClassName([styles.klaviyoLogo, styles.featuredKlaviyoLogo])}
        iconName="BugMarkFilled"
        role="img"
      />
    </div>
  );
};

const BlogCard = ({
  aspectRatio = '1.5 / 1',
  authorName = '',
  authorPhoto,
  authorUri = '',
  backgroundColor = 'light',
  categoryName = '',
  categoryLink = '',
  children,
  className,
  ctaText = 'Read',
  date,
  excerpt = '',
  eyebrow,
  featuredImage,
  featuredImageSrc,
  openInNewTab = false,
  readTime = 0,
  title,
  uri = '',
  cardTitleSize,
  ...props
}: IBlogCard) => {
  const { t } = useTranslation();

  const translateCtaText = ctaText === 'Read' ? t('molecules_blogCard_read') : ctaText;
  const authors = [{ image: authorPhoto, link: authorUri, name: authorName }];

  return (
  <div className={generateClassName(['blogCard', className, styles.blogCard, styles[backgroundColor]])} {...props}>

    <AnimationOutline
      buttonText={translateCtaText}
      className={styles.cardImageAnimation}
      openInNewTab={openInNewTab}
      outline={false}
      style={{ aspectRatio }}
      url={uri}
    >
      <BlogCardImage
        aspectRatio={aspectRatio}
        backgroundColor={backgroundColor}
        featuredImage={featuredImage}
        featuredImageSrc={featuredImageSrc}
      />
    </AnimationOutline>

    {Boolean(categoryName) && (
      <BlogCategoryButton
        afterText={<TranslatedDate date={date}/>}
        buttonTag={categoryLink ? '' : 'div'}
        className={styles.categoryButton}
        link={categoryLink}
        name={categoryName}
        backgroundColor={backgroundColor}
      />
    )}

    {Boolean(eyebrow) && eyebrow}

    <Anchor
      className={generateClassName([styles.title, 'semiBold', cardTitleSize === 'h6' ? 'h6' : 'h5'])}
      href={uri}
      tabIndex={-1}
      target={openInNewTab ? '_blank' : undefined}
    >
      {title}
    </Anchor>

    {Boolean(excerpt) && (
      <div
        className={generateClassName([styles.excerpt, 'p2'])}
        dangerouslySetInnerHTML={{ __html: excerpt }}
      />
    )}

    {Boolean(authorName && authorUri) && (
      <BlogAuthorThumbnail
        authors={authors}
        backgroundColor={backgroundColor}
        readTime={readTime}
      />
    )}

    {Boolean(children) && children}

  </div>
  );
};

export default BlogCard;
